import { apiRequest } from './client';
import type {
  CatalogCollection,
  CatalogExport,
  CatalogImportResult,
  CatalogImportStrategy,
  CatalogSummary,
} from '../types';

export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

export function summarizeCatalog(data: unknown): CatalogSummary {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(
      'Die Datei muss ein JSON-Objekt mit characters, items, spells und/oder npcs sein.',
    );
  }
  const payload = data as Record<string, unknown>;
  const hasCatalog =
    'characters' in payload
    || 'items' in payload
    || 'spells' in payload
    || 'npcs' in payload;
  if (!hasCatalog) {
    throw new Error('Die Datei enthält keine Charaktere, Items, Zauber oder Gegner.');
  }
  const count = (key: 'characters' | 'items' | 'spells' | 'npcs') => {
    const value = payload[key];
    if (value === undefined) return 0;
    if (!Array.isArray(value)) {
      throw new Error(`${key} muss eine Liste sein.`);
    }
    return value.length;
  };
  return {
    characters: count('characters'),
    items: count('items'),
    spells: count('spells'),
    npcs: count('npcs'),
  };
}

export function formatImportResult(result: CatalogImportResult): string {
  const parts: string[] = [];
  const push = (label: string, counts?: CatalogImportResult['items']) => {
    if (!counts) return;
    const bits = [];
    if (counts.created) bits.push(`${counts.created} neu`);
    if (counts.updated) bits.push(`${counts.updated} aktualisiert`);
    if (counts.deleted) bits.push(`${counts.deleted} entfernt`);
    if (!bits.length) bits.push('unverändert');
    parts.push(`${label}: ${bits.join(', ')}`);
  };
  push('Charaktere', result.characters);
  push('Items', result.items);
  push('Zauber', result.spells);
  push('Gegner', result.npcs);
  return parts.length ? `Import abgeschlossen. ${parts.join(' · ')}` : 'Import abgeschlossen.';
}

export function fetchCatalogExport(include?: CatalogCollection[]) {
  const query = include?.length ? `?include=${include.join(',')}` : '';
  return apiRequest<CatalogExport>(`/api/admin/export${query}`, { auth: true });
}

export function importCatalog(payload: CatalogExport, strategy: CatalogImportStrategy) {
  return apiRequest<CatalogImportResult>('/api/admin/import', {
    method: 'POST',
    auth: true,
    body: { ...payload, strategy },
  });
}

export function downloadJsonFile(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function catalogFilename(exportedAt?: string) {
  const stamp = (exportedAt || '').slice(0, 10).replace(/-/g, '') ||
    new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `ralf-katalog-${stamp}.json`;
}
